<template>
  <geo-json-source
    v-if="formattedData"
    :source-id="sourceName"
    :data="formattedData"
    :buffer="2"
    generate-id
  >
    <layer-group :layers="layers">
      <template #hover="{ feature }">
        <JobTripMapHover :hovered-feature="feature" />
      </template>
    </layer-group>
  </geo-json-source>
</template>

<script setup lang="ts">
import { useGetJobGeoJson } from '@/api/field-view'
import GeoJsonSource from '@/components/field-view-next/mapbox/GeoJsonSource.vue'
import LayerGroup from '@/components/field-view-next/mapbox/LayerGroup.vue'
import JobTripMapHover from '@/components/field-view-next/hovers/JobTripMapHover.vue'
import { computed, onBeforeUnmount, ref, toRef, watch, watchEffect } from 'vue'
import length from '@turf/length'
import { debounce } from 'lodash'
import { useMap } from '../mapbox/context'
import { useFieldView } from '../FieldViewState'
import { useStore } from 'vuex-composition-helpers'
import Job from '@/models/job'
import { getJobGeoJsonCacheTime } from '@/utils/job-geojson-cache-time'
import { createJobTripLayers, formatJobTripGeoJson } from './jobTripLayers'
import mapboxgl, { LayerSpecification } from 'mapbox-gl'

const props = defineProps<{
  selectedJobNumber: string | number
}>()

const emits = defineEmits<{
  (event: 'total-distance-calculated', data: number)
}>()

const { map, mini } = useMap()
const { setSelectedLocation, setSelectedResource } = useFieldView()

const jobNumber = toRef(props, 'selectedJobNumber')
const store = useStore()
const job = computed<Job | null | undefined>(() => store.getters['jobs/getById'](jobNumber.value))
const cacheTime = computed(() => getJobGeoJsonCacheTime(job.value?.status))
const { data } = useGetJobGeoJson(jobNumber, { cacheTime })

const sourceName = computed(() => `job-${jobNumber.value}`)
const layerIdPrefix = computed(() => sourceName.value)
const lastFittedBoundsKey = ref<string | null>(null)

const layers = computed<LayerSpecification[]>(() =>
  createJobTripLayers({
    sourceName: sourceName.value,
    layerIdPrefix: layerIdPrefix.value,
    includeLocations: true,
    locationTypes: ['DestinationLocation', 'PickupLocation', 'LocationVisitedEvent'],
  }),
)

const formattedData = computed(() => formatJobTripGeoJson(data.value))

const fitJobBounds = () => {
  if (!map.value || !formattedData.value?.features.length) return

  const bounds = new mapboxgl.LngLatBounds()

  formattedData.value.features.forEach((feature) => {
    if (feature.geometry.type === 'Point') {
      bounds.extend(feature.geometry.coordinates as [number, number])
    } else if (feature.geometry.type === 'LineString') {
      for (const coordinate of feature.geometry.coordinates) {
        bounds.extend(coordinate as [number, number])
      }
    } else if (feature.geometry.type === 'MultiLineString') {
      for (const group of feature.geometry.coordinates) {
        for (const coordinate of group) {
          bounds.extend(coordinate as [number, number])
        }
      }
    }
  })

  if (bounds.isEmpty()) return

  const boundsKey = `${mini.value ? 'mini' : 'full'}:${bounds
    .toArray()
    .flat()
    .map((coordinate) => coordinate.toFixed(6))
    .join(':')}`

  if (boundsKey === lastFittedBoundsKey.value) return

  try {
    const mapFitOptions = mini.value
      ? { padding: 60 }
      : {
          padding: {
            top: 150,
            bottom: 150,
            left: 150,
            right: 150 + 500,
          },
        }

    map.value.fitBounds(bounds, {
      ...mapFitOptions,
      maxZoom: 16,
    })

    lastFittedBoundsKey.value = boundsKey

    // Avoid showing selected pins twice in the same place.
    setSelectedLocation(null)
    setSelectedResource(null)
  } catch (_) {}
}

const debouncedFitJobBounds = debounce(() => {
  fitJobBounds()
}, 400)

watch(jobNumber, () => {
  lastFittedBoundsKey.value = null
})

watch(
  [() => formattedData.value?.features, () => map.value, () => mini.value],
  ([features, activeMap], _oldValue, onCleanup) => {
    if (!features?.length || !activeMap) return

    if (lastFittedBoundsKey.value == null) {
      fitJobBounds()
    } else {
      debouncedFitJobBounds()
    }

    onCleanup(() => {
      debouncedFitJobBounds.cancel()
    })
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  debouncedFitJobBounds.cancel()
})

watchEffect(() => {
  if (data.value && data.value.features.length > 0) {
    const lineStrings = data.value.features.filter(
      (feature) => feature.properties?.Type === 'Route',
    )

    const totalDistance = lineStrings.reduce((acc, feature) => acc + length(feature), 0)
    emits('total-distance-calculated', totalDistance)
  }
})
</script>

<style scoped></style>
